import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ContentCard from "Components/shared/ContentCard";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
} from "@mui/material";
import { useRTL } from "hooks/isRTL";
import {
  educationEnum,
  fileTypeEnum,
  genderEnum,
  RegistrationStatusEnum,
} from "enums/shared";
import { approveDriver, getDriverDetails, changeDriverType } from "services/driverService";
import LoadingBanner from "Components/shared/LoadingBanner";
import { getFile } from "services/customerManagement";
import { useState } from "react";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import { fallbackValue } from "constants/fallbackConstant";
import formatDateAndTime from "utils/formatDateAndTime";

const DriverInfoTab = () => {
  const { driverId } = useParams();
  const queryClient = useQueryClient();
  const { t } = useRTL();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    message: "",
    severity: "",
  });

  const {
    data: driverData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["driverDetails", driverId],
    queryFn: () => getDriverDetails(driverId),
    enabled: !!driverId,
  });

  const { mutate: approveDriverAction } = useMutation({
    mutationFn: () => approveDriver(driverId),
    onSuccess: () => {
      setSnackbarDetails({
        message: t("Driver approved successfully!"),
        severity: "success",
      });
      queryClient.invalidateQueries(["driverDetails", driverId]);
    },
    onError: () => {
      setSnackbarDetails({
        message: t("Failed to approve driver. Please try again."),
        severity: "error",
      });
    },
  });

  // Change driver type (Regular <-> Expedited)
  const { mutate: changeType, isLoading: isChangingType } = useMutation({
    mutationFn: ({ driverId: dId, newType }) => changeDriverType(dId, newType),
    onSuccess: (res) => {
      setSnackbarDetails({
        message: res?.message || t("Driver type updated successfully"),
        severity: "success",
      });
      queryClient.invalidateQueries(["driverDetails", driverId]);
    },
    onError: () => {
      setSnackbarDetails({
        message: t("Failed to change driver type. Please try again."),
        severity: "error",
      });
    },
  });

  const handleFileClick = async (fileId) => {
    try {
      const fileBlob = await getFile(fileId);
      const fileURL = window.URL.createObjectURL(fileBlob);
      window.open(fileURL, "_blank");
    } catch (error) {
      console.error("Failed to fetch file:", error);
    }
  };

  if (isLoading) return <LoadingBanner isLoading={true} message="Loading..." />;
  if (isError)
    return <Typography color="error">Failed to load data.</Typography>;

  const content = [
    {
      label: t("driverfullName"),
      value: driverData?.fullName || fallbackValue,
    },
    {
      label: t("driverphoneNumber"),
      value: driverData?.phoneNumber?.replace("_", "") || fallbackValue,
    },

    {
      label: t("gender"),
      value: t(genderEnum[driverData?.gender]) || "Unknown",
    },
    {
      label: t("birthday"),
      value: driverData?.birthday
        ? driverData.birthday.split("-").reverse().join("/")
        : fallbackValue,
    },
    { label: t("age"), value: driverData?.age ?? fallbackValue },
    {
      label: t("drivernationalNumber"),
      value: driverData?.nationalNumber || fallbackValue,
    },
    {
      label: t("cityArea"),
      value: driverData?.city || fallbackValue,
    },
    {
      label: t("vehicleType"),
      value: driverData?.vehicleTypeLookup?.nameEn || fallbackValue,
    },
    {
      label: t("education"),
      value: t(educationEnum[driverData?.education]) || fallbackValue,
    },
    {
      label: t("englishSpeaker"),
      value: driverData?.englishSpeaker ? t("Yes") : t("no"),
    },
    {
      label: t("registrationStatus"),
      value: driverData?.status
        ? t(
            Object.keys(RegistrationStatusEnum).find(
              (key) => RegistrationStatusEnum[key] === driverData?.status
            ) || "Unknown"
          )
        : "Unknown",
    },
    {
      label: t("creationDate"),
      value: formatDateAndTime(driverData?.creationDate),
    },
  ];

  const handleApprove = () => {
    approveDriverAction();
    setDialogOpen(false);
  };

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  const handleSnackbarClose = () =>
    setSnackbarDetails({ message: "", severity: "" });

  const requiredFields = [
    "fullName",
    "phoneNumber",
    "gender",
    "birthday",
    "nationalNumber",
    "city",
    "vehicleTypeLookupId",
  ];

  const hasMissingRequiredFields = requiredFields.some(
    (field) => !driverData?.[field]
  );

  const shouldDisableApproveButton =
    driverData?.status === RegistrationStatusEnum.active ||
    !driverData?.driverFiles?.length ||
    hasMissingRequiredFields;
  return (
    <Box sx={{ padding: "1rem" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "2rem",
        }}
      >
        {/* Personal Information */}
        <ContentCard title={t("personalInformation")} content={content} />

        {/* Driver Files & Approve Button */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            padding: "1.5rem",
            backgroundColor: "background.white",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Driver Type Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">{t("driverType")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">{t("Regular")}</Typography>
              <Switch
                checked={driverData?.driverType === 2}
                onChange={(e) => {
                  const newType = e.target.checked ? 2 : 1; // 2 => Expedited, 1 => Regular
                  changeType({ driverId, newType });
                }}
                color="primary"
                disabled={isChangingType}
              />
              <Typography variant="body2">{t("Expedited")}</Typography>
            </Box>
          </Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              marginBottom: "1rem",
              color: "text.black",
              fontSize: "1.2rem",
            }}
          >
            {t("files")}
          </Typography>
          <List sx={{ flexGrow: 1 }}>
            {driverData?.driverFiles?.length > 0 ? (
              driverData.driverFiles.map((file) => (
                <ListItem
                  key={file.id}
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "center",
                    borderBottom: "1px solid #e0e0e0",
                    padding: "0.5rem 0 0.75rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      color: "text.black",
                      fontSize: "1rem",
                      minWidth: "80px",
                    }}
                  >
                    {fileTypeEnum[file.fileType]
                      ? t(fileTypeEnum[file.fileType])
                      : t("Unknown File")}
                  </Typography>
                  <IconButton
                    onClick={() => handleFileClick(file.id)}
                    sx={{ color: "primary.main" }}
                  >
                    <InsertDriveFileIcon />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.9rem",
                    }}
                  >
                    {`${(file.size / 1024).toFixed(1)} KB`}
                  </Typography>
                </ListItem>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("noDocumentsAvailable")}
              </Typography>
            )}
          </List>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#2A3467",
              "&:hover": { backgroundColor: "#1f284f" },
              marginTop: "1.5rem",
            }}
            onClick={handleOpenDialog}
            disabled={shouldDisableApproveButton}
          >
            {driverData?.status === RegistrationStatusEnum.active
              ? t("approved")
              : t("approve")}
          </Button>
        </Paper>
      </Box>

      {/* Approve Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{t("confirmAction")}</DialogTitle>
        <DialogContent>
          <Typography>{t("areYouSureYouWantToApprove")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleCloseDialog}>
            {t("decline")}
          </Button>
          <Button variant="contained" color="success" onClick={handleApprove}>
            {t("approve")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <CustomSnackbar
        message={snackbarDetails.message}
        severity={snackbarDetails.severity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default DriverInfoTab;
